// src/component/IdleTimeout.jsx
//
// v1.4.1 C7b: the idle session timeout, mounted once by DashboardLayout.
// Counts ONLY user input — keyboard, pointer, touch, focus — through
// services/idleSession.js (one clock for every tab). Polling, websocket and
// background requests never touch it. At the limit minus 60 s a warning
// offers Stay signed in / Sign out; at the limit the session ends (the server
// told, the keys cleared, /login says why). An open M-Pesa payment wait
// defers the sign-out until it settles or 3 minutes pass. The server holds
// the same limit on every refresh (apps/accounts/session_limits.py).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { stopActiveTour } from '../tours/runTour';
import {
  PAY_WAIT_GRACE_MS, WARNING_MS, clock, followSignOut, idleLimitMs, isPayWaiting,
  lastActivity, markActivity, signOut, subscribe,
} from '../services/idleSession';

const INPUT_EVENTS = ['keydown', 'pointerdown', 'pointermove', 'touchstart', 'wheel', 'focusin'];

export default function IdleTimeout() {
  const { user } = useAuth();
  const limit = idleLimitMs(user);
  const [warning, setWarning] = useState(false);
  const [left, setLeft] = useState(60);
  const warningRef = useRef(false);
  const endedRef = useRef(false);
  const dialogRef = useRef(null);
  const stayRef = useRef(null);
  const returnFocus = useRef(null);

  const wasPaying = useRef(false);
  const payEndedAt = useRef(null);

  const end = () => {
    endedRef.current = true;
    show(false);
    signOut('idle');
  };

  const show = useCallback((on, seconds) => {
    warningRef.current = on;
    setWarning(on);
    if (seconds !== undefined) setLeft(seconds);
  }, []);

  const check = useCallback(() => {
    if (endedRef.current) return;
    const now = clock.now();
    const last = lastActivity() || now;
    // Never interrupt an open M-Pesa wait: no warning over the pay dialog,
    // no sign-out until it settles or PAY_WAIT_GRACE_MS past the limit; once
    // it settles the person still gets a full minute's warning, so they see
    // the payment's result before anything happens.
    const paying = isPayWaiting();
    if (wasPaying.current && !paying) payEndedAt.current = now;
    wasPaying.current = paying;
    if (paying) {
      if (now - (last + limit) >= PAY_WAIT_GRACE_MS) end();
      else if (warningRef.current) show(false);
      return;
    }
    let deadline = last + limit;
    if (payEndedAt.current && payEndedAt.current > last) {
      deadline = Math.max(deadline, payEndedAt.current + WARNING_MS);
    }
    if (now >= deadline) end();
    else if (now >= deadline - WARNING_MS) show(true, Math.max(1, Math.ceil((deadline - now) / 1000)));
    else if (warningRef.current) show(false);
  }, [limit, show]);   // eslint-disable-line react-hooks/exhaustive-deps

  // A session starts its clock; then check every second.
  useEffect(() => {
    if (!lastActivity()) markActivity({ force: true });
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [check]);

  // Input on this page. While the warning is up only its buttons answer —
  // a stray mouse movement must not dismiss it unseen.
  useEffect(() => {
    const onInput = () => { if (!warningRef.current && !endedRef.current) markActivity(); };
    INPUT_EVENTS.forEach((t) => window.addEventListener(t, onInput, { capture: true, passive: true }));
    return () => INPUT_EVENTS.forEach((t) => window.removeEventListener(t, onInput, { capture: true }));
  }, []);

  // The other tabs: their input resets this tab, their sign-out ends it.
  useEffect(() => subscribe({
    onActivity: () => check(),
    onSignOut: (reason) => {
      if (endedRef.current) return;
      endedRef.current = true;
      show(false);
      followSignOut(reason);
    },
  }), [check, show]);

  // Focus into the dialog when it opens, back where it was when it closes.
  useEffect(() => {
    if (warning) {
      stopActiveTour();   // a running tour would block the buttons (driver.js)
      returnFocus.current = document.activeElement;
      stayRef.current?.focus();
    } else if (returnFocus.current) {
      returnFocus.current.focus?.();
      returnFocus.current = null;
    }
  }, [warning]);

  const stay = () => {
    markActivity({ force: true });
    show(false);
  };
  const signOutNow = () => {
    endedRef.current = true;
    show(false);
    signOut('user');
  };
  const onKeyDown = (e) => {
    // Escape = stay — and only that: the dialog underneath must not close too.
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); stay(); return; }
    if (e.key !== 'Tab') return;
    const buttons = [...(dialogRef.current?.querySelectorAll('button') || [])];
    if (!buttons.length) return;
    const i = buttons.indexOf(document.activeElement);
    e.preventDefault();
    const next = e.shiftKey ? (i <= 0 ? buttons.length - 1 : i - 1) : (i === buttons.length - 1 ? 0 : i + 1);
    buttons[next].focus();
  };

  if (!warning) return null;
  // W-1: a tenant's dialog names their landlord, never the platform.
  const landlord = user?.role === 'tenant' ? user?.brand?.name : null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4">
      <div ref={dialogRef} role="alertdialog" aria-modal="true" aria-labelledby="idle-title"
        aria-describedby="idle-desc" onKeyDown={onKeyDown}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-gray-100">
          <h2 id="idle-title" className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Clock size={17} className="text-amber-600 flex-shrink-0" aria-hidden="true" />
            You'll be signed out in {left} {left === 1 ? 'second' : 'seconds'}
          </h2>
          {landlord && <p className="text-xs text-gray-500 mt-0.5">{landlord}</p>}
        </div>
        <div className="p-5 space-y-4">
          <p id="idle-desc" className="text-sm text-gray-600">
            You haven't used the app for a while, so you'll be signed out to keep your account safe.
          </p>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={signOutNow} className="flex-1 btn-secondary">Sign out</button>
            <button type="button" ref={stayRef} onClick={stay} className="flex-1 btn-primary">Stay signed in</button>
          </div>
        </div>
      </div>
    </div>
  );
}
